import { EventPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { PublicBaseEventSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import { enums } from "@shared/constants";
import { useMemo } from "react";
import {
  eventDisplayLowInventory,
  eventHasWaitlistItems,
  isPastEvent,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import {
  isTicketTypeSoldOut,
  ticketDisplayLowInventory,
} from "@shared/modules/ticket-types/utils/ticket-type-utils";
import { InventoryBadgeProps } from "./InventoryBadge.props";

type EventsWithInventoryType =
  | PublicBaseEventSerializer
  | VenueBasedEventSerializer
  | EventPropertyMap;

export function useInventoryBadge(props: InventoryBadgeProps) {
  const { tt, eventData, venueData } = props;

  // Low inventory is enabled on the venue if low_inventory_warning_enabled is True
  const enabledOnVenue = Boolean(venueData?.low_inventory_warning_enabled);

  // Low inventory is enabled on the event if low_inventory_warning_enabled is null or undefined (default values)
  // Low inventory is disabled on the event if low_inventory_warning_enabled is False
  const enabledOnEvent = Boolean(
    eventData?.low_inventory_warning_enabled === undefined ||
      eventData?.low_inventory_warning_enabled === null
  );

  // Low inventory is enabled on the ticket if low_inventory_warning_enabled is null or undefined (default values)
  // Low inventory is disabled on the ticket if low_inventory_warning_enabled is False
  const enabledOnTicket = Boolean(
    tt &&
      (tt.low_inventory_warning_enabled === undefined ||
        tt.low_inventory_warning_enabled === null)
  );

  // Check if low inventory is enabled at the venue, event, and ticket levels
  // All levels must be enabled for the ticket inventory thresholds to be displayed
  const ticketThresholdDisplaysEnabled =
    enabledOnVenue && enabledOnEvent && enabledOnTicket;

  // Check if low inventory is enabled at the venue and event levels
  // These levels must be enabled for the event inventory thresholds to be displayed
  const eventThresholdDisplaysEnabled = enabledOnVenue && enabledOnEvent;

  // remaining tickets text logic for individual TICKETS
  const showRemainingTicketsForTicket = Boolean(
    tt &&
      ticketThresholdDisplaysEnabled &&
      !isTicketTypeSoldOut(tt) &&
      ticketDisplayLowInventory(tt).displayValue &&
      !tt.has_payment_plans &&
      eventData.region_is_allowed
  );

  // remaining tickets text logic for EVENTS
  const showRemainingTicketsForEvent =
    eventThresholdDisplaysEnabled &&
    !isSoldOutEvent(eventData) &&
    !isPastEvent<EventsWithInventoryType>(eventData) &&
    eventDisplayLowInventory(eventData).displayValue &&
    !eventHasWaitlistItems(eventData) &&
    !eventData.external_link;

  // show the remaining tickets text component for tickets OR events
  const showRemainingTickets =
    showRemainingTicketsForTicket || showRemainingTicketsForEvent;

  // low inventory component logic for individual TICKETS
  const showLowTicketInventory = Boolean(
    tt &&
      ticketThresholdDisplaysEnabled &&
      tt.low_ticket_inventory !==
        enums.REMAINING_TICKETS_PERCENTAGE_MAP.NO_WARNING.id &&
      !isTicketTypeSoldOut(tt) &&
      !ticketDisplayLowInventory(tt).displayValue &&
      !tt.has_payment_plans &&
      eventData.region_is_allowed
  );

  // low inventory component logic for EVENTS
  const showLowEventInventory = Boolean(
    eventThresholdDisplaysEnabled &&
      "low_ticket_inventory" in eventData &&
      eventData.low_ticket_inventory !==
        enums.REMAINING_TICKETS_PERCENTAGE_MAP.NO_WARNING.id &&
      !isSoldOutEvent(eventData) &&
      !isPastEvent(eventData) &&
      !eventDisplayLowInventory(eventData).displayValue &&
      !eventHasWaitlistItems(eventData) &&
      !eventData.external_link
  );

  // show the low inventory component for tickets OR events
  const showLowInventory = showLowTicketInventory || showLowEventInventory;

  // low inventory value for an individual ticket OR for an entire event
  const lowTicketInventory = useMemo(() => {
    if (showLowTicketInventory && tt) {
      return tt.low_ticket_inventory;
    }
    if (showLowEventInventory && "low_ticket_inventory" in eventData) {
      return eventData.low_ticket_inventory;
    }

    return undefined;
  }, [showLowEventInventory, showLowTicketInventory, eventData, tt]);

  // number of remaining tickets for an individual ticket OR for an entire event
  const numOfRemainingTickets = useMemo(() => {
    if (showRemainingTicketsForTicket && tt) {
      return ticketDisplayLowInventory(tt).inventoryValue ?? 0;
    }
    if (showRemainingTicketsForEvent) {
      return eventDisplayLowInventory(eventData).inventoryValue ?? 0;
    }

    return undefined;
  }, [tt, ticketDisplayLowInventory]);

  return {
    showLowInventory,
    showRemainingTickets,
    numOfRemainingTickets,
    lowTicketInventory,
    lowThreshold: venueData?.low_inventory_threshold_low,
    middleThreshold: venueData?.low_inventory_threshold_middle,
    highThreshold: venueData?.low_inventory_threshold_high,
  };
}
